import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, ShoppingCart, Edit, Trash2, CheckCircle, Send, Download, Eye, Package, Truck } from 'lucide-react';
import PageTour from '@/components/PageTour';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-info/20 text-info',
  sent: 'bg-indigo-100 text-indigo-700',
  received: 'bg-success/20 text-success',
  cancelled: 'bg-error/20 text-error',
};

const emptyItem = { description: '', quantity: 1, unit_price: 0 };

export default function PurchaseOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showReceive, setShowReceive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [catalogueItems, setCatalogueItems] = useState([]);
  const [pickerTab, setPickerTab] = useState('inventory');
  const [poDefaults, setPoDefaults] = useState({ terms: '', notes: '', tax_rate: 15 });
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [itemPickerIdx, setItemPickerIdx] = useState(null);
  const [itemPickerSearch, setItemPickerSearch] = useState('');
  const [receiveNotes, setReceiveNotes] = useState('');
  const [form, setForm] = useState({
    supplier_name: '', supplier_id: '', items: [{ ...emptyItem }],
    tax_rate: 15, delivery_date: '', notes: '', terms: '', source: 'accounting',
  });

  useEffect(() => { fetchData(); }, []);

  // Deep-link from the low-stock alert (Recipes → Record Production): open the
  // Create Purchase form, optionally pre-filling the short ingredient.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      resetForm();
      const itemName = searchParams.get('item_name');
      const unit = searchParams.get('unit');
      if (itemName) {
        setForm(f => ({ ...f, items: [{ ...f.items[0], description: itemName, item_name: itemName, unit: unit || 'piece' }] }));
      }
      setShowForm(true);
      searchParams.delete('new'); searchParams.delete('item_name'); searchParams.delete('unit');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [poRes, sRes, invRes, catRes, defRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/suppliers').catch(() => ({ data: [] })),
        api.get('/stock/balance').catch(() => ({ data: [] })),
        api.get('/items').catch(() => ({ data: [] })),
        api.get('/settings/po-defaults').catch(() => ({ data: { terms: '', notes: '', tax_rate: 15 } })),
      ]);
      setOrders(poRes.data);
      setSuppliers(Array.isArray(sRes.data) ? sRes.data : sRes.data?.data || []);
      setInventoryItems(Array.isArray(invRes.data) ? invRes.data : []);
      setCatalogueItems(Array.isArray(catRes.data) ? catRes.data : []);
      setPoDefaults(defRes.data || { terms: '', notes: '', tax_rate: 15 });
    } catch { toast.error('Failed to load purchase orders'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({
      supplier_name: '', supplier_id: '', items: [{ ...emptyItem }],
      tax_rate: poDefaults.tax_rate ?? 15,
      delivery_date: '',
      notes: poDefaults.notes || '',
      terms: poDefaults.terms || '',
      source: 'accounting',
    });
    setEditing(null);
  };

  const saveAsDefault = async () => {
    setSavingDefaults(true);
    try {
      await api.put('/settings/po-defaults', { terms: form.terms, notes: form.notes, tax_rate: form.tax_rate });
      setPoDefaults({ terms: form.terms, notes: form.notes, tax_rate: form.tax_rate });
      toast.success('Saved as default for future POs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save defaults');
    } finally { setSavingDefaults(false); }
  };

  const pickInventoryItem = (idx, item) => {
    const unit_price = Number(item.avg_cost) || Number(item.last_cost) || Number(item.unit_price) || 0;
    updateItem(idx, 'description', item.item_name || item.name || '');
    updateItem(idx, 'unit_price', unit_price);
    updateItem(idx, 'item_id', item.item_id || item.id || '');
    setItemPickerIdx(null);
    setItemPickerSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.items.some(i => i.description && i.quantity > 0)) { toast.error('Add at least one item'); return; }
    try {
      if (editing) { await api.put(`/purchase-orders/${editing.id}`, form); toast.success('PO updated'); }
      else { await api.post('/purchase-orders', form); toast.success('PO created'); }
      setShowForm(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (po) => {
    setEditing(po);
    setForm({ supplier_name: po.supplier_name, supplier_id: po.supplier_id || '', items: po.items || [{ ...emptyItem }], tax_rate: po.tax_rate || 0, delivery_date: po.delivery_date || '', notes: po.notes || '', terms: po.terms || '', source: po.source || 'accounting' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PO?')) return;
    try { await api.delete(`/purchase-orders/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.put(`/purchase-orders/${id}/status`, { status }); toast.success(`Status: ${status}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReceive = async (id) => {
    try {
      await api.put(`/purchase-orders/${id}/receive`, { notes: receiveNotes });
      toast.success('PO marked as received');
      setShowReceive(null); setReceiveNotes(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleExport = async (id) => {
    try {
      const res = await api.get(`/purchase-orders/${id}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `PO_${id}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('Export failed'); }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => setForm(f => ({ ...f, items: f.items.map((item, i) => i === idx ? { ...item, [field]: val } : item) }));

  const calcSubtotal = () => form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const calcTax = () => (calcSubtotal() * (form.tax_rate || 0)) / 100;
  const calcTotal = () => calcSubtotal() + calcTax();

  const filtered = orders.filter(po => {
    if (filterStatus !== 'all' && po.status !== filterStatus) return false;
    if (search && !po.po_number?.toLowerCase().includes(search.toLowerCase()) && !po.supplier_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="purchase-orders-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-700 flex items-center justify-center shadow-lg">
              <ShoppingCart size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight" data-testid="po-title">Purchase Orders</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Create, track, and manage purchase orders</p>
            </div>
          </div>
          <Button className="rounded-full" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-po-btn">
            <Plus size={18} className="mr-2" />New PO
          </Button>
        </div>
        <div className="flex gap-2">
          <PageTour storageKey="tour_po" steps={[
            { target: '[data-testid="add-po-btn-2"]', title: 'Create Purchase Order', content: 'Create a new purchase order for a supplier with items, quantities, and delivery date.' },
            { target: '[data-testid="po-search"]', title: 'Search & Filter', content: 'Search POs by number or supplier name. Filter by status (draft, approved, sent, received).' },
            { target: '[data-testid="po-table"]', title: 'PO List', content: 'Manage all purchase orders. Approve, send to supplier, mark as received, or export as PDF.' },
          ]} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['draft', 'approved', 'sent', 'received', 'cancelled'].map(s => (
            <Card key={s} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground capitalize">{s}</p>
                <p className="text-2xl font-bold mt-1">{orders.filter(po => po.status === s).length}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input placeholder="Search POs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="po-search-2" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="po-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* PO List */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="po-table-2">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">PO #</th>
                    <th className="p-3 text-left font-medium">Supplier</th>
                    <th className="p-3 text-left font-medium">Items</th>
                    <th className="p-3 text-right font-medium">Total</th>
                    <th className="p-3 text-left font-medium">Delivery</th>
                    <th className="p-3 text-left font-medium">Status</th>
                    <th className="p-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(po => (
                    <tr key={po.id} className="border-b hover:bg-muted/30 transition-colors" data-testid="po-row">
                      <td className="p-3 font-mono text-xs">{po.po_number}</td>
                      <td className="p-3">{po.supplier_name || '-'}</td>
                      <td className="p-3 text-muted-foreground">{po.items?.length || 0} items</td>
                      <td className="p-3 text-right font-semibold">{po.currency_code} {po.total?.toFixed(2)}</td>
                      <td className="p-3 text-muted-foreground text-xs">{po.delivery_date || '-'}</td>
                      <td className="p-3"><Badge className={STATUS_COLORS[po.status] || 'bg-gray-100'} data-testid="po-status">{po.status}</Badge></td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setShowDetail(po)} data-testid="view-po-btn"><Eye size={14} /></Button>
                          {po.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => handleEdit(po)} data-testid="edit-po-btn"><Edit size={14} /></Button>}
                          {po.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(po.id, 'approved')} title="Approve"><CheckCircle size={14} className="text-info" /></Button>}
                          {po.status === 'approved' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(po.id, 'sent')} title="Mark Sent"><Send size={14} className="text-indigo-500" /></Button>}
                          {po.status === 'sent' && <Button size="sm" variant="ghost" onClick={() => { setShowReceive(po); setReceiveNotes(''); }} title="Receive"><Package size={14} className="text-success" /></Button>}
                          <Button size="sm" variant="ghost" onClick={() => handleExport(po.id)} title="Export PDF"><Download size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(po.id)} className="text-error" data-testid="delete-po-btn"><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No purchase orders found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="po-form-dialog">
            <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'New'} Purchase Order</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Supplier Name *</Label>
                  <Input value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} required placeholder="Supplier name" data-testid="po-supplier-input" />
                </div>
                <div>
                  <Label>Supplier (from list)</Label>
                  <Select value={form.supplier_id} onValueChange={v => { const s = suppliers.find(x => x.id === v); setForm({ ...form, supplier_id: v, supplier_name: s?.name || form.supplier_name }); }}>
                    <SelectTrigger><SelectValue placeholder="Select existing" /></SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tax Rate (%)</Label>
                  <Input type="number" step="0.1" min="0" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Delivery Date</Label>
                  <Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} data-testid="po-delivery-input" />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-base font-semibold">Items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="po-add-item-btn"><Plus size={14} className="mr-1" />Add Item</Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5 flex gap-1">
                        <Input className="flex-1" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} data-testid={`po-item-desc-${idx}`} />
                        <Button type="button" size="sm" variant="outline" className="px-2" title="Pick from inventory" onClick={() => { setItemPickerIdx(idx); setItemPickerSearch(''); }} data-testid={`po-pick-inv-${idx}`}>
                          <Package size={14} />
                        </Button>
                      </div>
                      <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)} />
                      <Input className="col-span-3" type="number" step="0.01" min="0" placeholder="Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                      <span className="col-span-1 text-sm font-semibold text-right">{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-error" onClick={() => removeItem(idx)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right space-y-1 text-sm">
                  <p>Subtotal: <span className="font-semibold">{calcSubtotal().toFixed(2)}</span></p>
                  <p>Tax ({form.tax_rate}%): <span className="font-semibold">{calcTax().toFixed(2)}</span></p>
                  <p className="text-base font-bold">Total: {calcTotal().toFixed(2)}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Terms</Label>
                  <Button type="button" size="sm" variant="ghost" className="text-xs h-7" onClick={saveAsDefault} disabled={savingDefaults} data-testid="po-save-defaults-btn">
                    Save terms/notes as default
                  </Button>
                </div>
                <Textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} placeholder="Payment/delivery terms..." rows={2} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." rows={2} />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="rounded-full" data-testid="submit-po-btn">{editing ? 'Update' : 'Create'} PO</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="po-detail-dialog">
            {showDetail && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-outfit flex items-center gap-3">
                    {showDetail.po_number}
                    <Badge className={STATUS_COLORS[showDetail.status]}>{showDetail.status}</Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{showDetail.supplier_name || '-'}</span></div>
                    <div><span className="text-muted-foreground">Created:</span> <span className="font-medium">{showDetail.created_at?.slice(0, 10)}</span></div>
                    <div><span className="text-muted-foreground">Delivery:</span> <span className="font-medium">{showDetail.delivery_date || '-'}</span></div>
                    <div><span className="text-muted-foreground">Created By:</span> <span className="font-medium">{showDetail.created_by_name}</span></div>
                    {showDetail.received_at && <div><span className="text-muted-foreground">Received:</span> <span className="font-medium">{showDetail.received_at?.slice(0, 10)}</span></div>}
                    {showDetail.received_by && <div><span className="text-muted-foreground">Received By:</span> <span className="font-medium">{showDetail.received_by}</span></div>}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead><tr className="bg-muted/50"><th className="p-2 text-left">#</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                      <tbody>
                        {showDetail.items?.map((item, i) => (
                          <tr key={i} className="border-t"><td className="p-2">{i + 1}</td><td className="p-2">{item.description}</td><td className="p-2 text-right">{item.quantity}</td><td className="p-2 text-right">{item.unit_price?.toFixed(2)}</td><td className="p-2 text-right font-semibold">{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="text-right space-y-1 text-sm">
                    <p>Subtotal: <span className="font-semibold">{showDetail.currency_code} {showDetail.subtotal?.toFixed(2)}</span></p>
                    <p>Tax ({showDetail.tax_rate}%): <span className="font-semibold">{showDetail.tax_amount?.toFixed(2)}</span></p>
                    <p className="text-lg font-bold">{showDetail.currency_code} {showDetail.total?.toFixed(2)}</p>
                  </div>

                  {showDetail.receive_notes && <div className="p-3 bg-success/10 rounded-lg border border-success/30"><p className="text-xs text-success mb-1">Receiving Notes</p><p className="text-sm">{showDetail.receive_notes}</p></div>}
                  {showDetail.notes && <div className="p-3 bg-muted/50 rounded-lg"><p className="text-xs text-muted-foreground mb-1">Notes</p><p className="text-sm">{showDetail.notes}</p></div>}

                  <div className="flex gap-2 flex-wrap">
                    {showDetail.status === 'draft' && <Button size="sm" onClick={() => { handleStatusChange(showDetail.id, 'approved'); setShowDetail(null); }}><CheckCircle size={14} className="mr-1" />Approve</Button>}
                    {showDetail.status === 'approved' && <Button size="sm" onClick={() => { handleStatusChange(showDetail.id, 'sent'); setShowDetail(null); }}><Send size={14} className="mr-1" />Mark Sent</Button>}
                    {showDetail.status === 'sent' && <Button size="sm" className="bg-success hover:bg-green-700" onClick={() => { setShowReceive(showDetail); setShowDetail(null); }}><Truck size={14} className="mr-1" />Receive</Button>}
                    <Button size="sm" variant="outline" onClick={() => handleExport(showDetail.id)}><Download size={14} className="mr-1" />PDF</Button>
                    {showDetail.status !== 'cancelled' && showDetail.status !== 'received' && <Button size="sm" variant="destructive" onClick={() => { handleStatusChange(showDetail.id, 'cancelled'); setShowDetail(null); }}>Cancel PO</Button>}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Inventory Item Picker */}
        <Dialog open={itemPickerIdx !== null} onOpenChange={(o) => { if (!o) setItemPickerIdx(null); }}>
          <DialogContent className="max-w-lg max-h-[75dvh] overflow-y-auto" data-testid="po-inv-picker-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Pick Item</DialogTitle></DialogHeader>
            <Tabs value={pickerTab} onValueChange={setPickerTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="inventory" data-testid="po-picker-tab-inventory">
                  From Inventory ({inventoryItems.length})
                </TabsTrigger>
                <TabsTrigger value="catalogue" data-testid="po-picker-tab-catalogue">
                  From Catalogue ({catalogueItems.length})
                </TabsTrigger>
              </TabsList>

              <div className="mt-3">
                <Input placeholder="Search items..." value={itemPickerSearch} onChange={e => setItemPickerSearch(e.target.value)} autoFocus data-testid="po-inv-picker-search" />
              </div>

              <TabsContent value="inventory" className="mt-3">
                <p className="text-[11px] text-muted-foreground mb-2">Items with stock entries — unit price defaults to avg cost from your stock history.</p>
                <div className="max-h-80 overflow-y-auto border rounded-lg">
                  {inventoryItems.length === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">No inventory items with stock entries yet. Switch to "From Catalogue" for the full item list.</div>
                  )}
                  {inventoryItems
                    .filter(it => {
                      const n = (it.item_name || it.name || '').toLowerCase();
                      return !itemPickerSearch || n.includes(itemPickerSearch.toLowerCase());
                    })
                    .slice(0, 50)
                    .map(it => (
                      <button
                        key={it.item_id || it.id}
                        type="button"
                        className="w-full text-left p-2 hover:bg-muted/50 border-b last:border-b-0 flex items-center justify-between gap-2"
                        onClick={() => pickInventoryItem(itemPickerIdx, it)}
                        data-testid={`po-inv-item-${it.item_id || it.id}`}
                      >
                        <div>
                          <div className="font-medium text-sm">{it.item_name || it.name}</div>
                          <div className="text-xs text-muted-foreground">{it.unit || ''} {it.category ? `· ${it.category}` : ''}</div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {it.balance != null && <div>Stock: {Number(it.balance).toFixed(2)}</div>}
                          {(it.avg_cost || it.last_cost || it.unit_price) ? (
                            <div className="text-right font-semibold">{(Number(it.avg_cost) || Number(it.last_cost) || Number(it.unit_price) || 0).toFixed(2)}</div>
                          ) : null}
                        </div>
                      </button>
                    ))}
                </div>
              </TabsContent>

              <TabsContent value="catalogue" className="mt-3">
                <p className="text-[11px] text-muted-foreground mb-2">Every item in your catalogue — useful for first-time purchases of items that don't have stock yet.</p>
                <div className="max-h-80 overflow-y-auto border rounded-lg">
                  {catalogueItems.length === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">No catalogue items yet. Add them in <a href="/items" className="underline text-primary">Items</a>.</div>
                  )}
                  {catalogueItems
                    .filter(it => {
                      const n = (it.name || '').toLowerCase();
                      const inactive = it.active === false;
                      if (inactive) return false;
                      return !itemPickerSearch || n.includes(itemPickerSearch.toLowerCase());
                    })
                    .slice(0, 100)
                    .map(it => (
                      <button
                        key={it.id}
                        type="button"
                        className="w-full text-left p-2 hover:bg-muted/50 border-b last:border-b-0 flex items-center justify-between gap-2"
                        onClick={() => pickInventoryItem(itemPickerIdx, { item_id: it.id, item_name: it.name, unit: it.unit || 'piece', category: it.category, unit_price: it.cost_price ?? it.unit_price })}
                        data-testid={`po-cat-item-${it.id}`}
                      >
                        <div>
                          <div className="font-medium text-sm">{it.name}</div>
                          <div className="text-xs text-muted-foreground">{it.unit || 'piece'} {it.category ? `· ${it.category}` : ''}</div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(it.cost_price || it.unit_price) ? (
                            <div className="text-right font-semibold">{(Number(it.cost_price) || Number(it.unit_price) || 0).toFixed(2)}</div>
                          ) : <div className="opacity-50">no price</div>}
                        </div>
                      </button>
                    ))}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Receive Dialog */}
        <Dialog open={!!showReceive} onOpenChange={() => setShowReceive(null)}>
          <DialogContent data-testid="receive-po-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Receive PO: {showReceive?.po_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="text-sm">
                <p><span className="text-muted-foreground">Supplier:</span> {showReceive?.supplier_name}</p>
                <p><span className="text-muted-foreground">Items:</span> {showReceive?.items?.length} items</p>
                <p className="font-semibold mt-1">Total: {showReceive?.currency_code} {showReceive?.total?.toFixed(2)}</p>
              </div>
              <div>
                <Label>Receiving Notes</Label>
                <Textarea value={receiveNotes} onChange={e => setReceiveNotes(e.target.value)} placeholder="Any notes about the delivery..." rows={3} />
              </div>
              <div className="flex gap-3">
                <Button className="bg-success hover:bg-green-700 rounded-full" onClick={() => handleReceive(showReceive.id)} data-testid="confirm-receive-btn">
                  <Package size={16} className="mr-2" />Confirm Received
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => setShowReceive(null)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
